import React, { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button, ButtonAdd } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useConfirm } from "@/components/ui/confirm-dialog";
import {
  cn,
  displayPrice,
  formatPrice,
  getImageUrl,
} from "@/lib/utils";
import { useModal, useRestaurantCurrency } from "@/lib/hooks";
import {
  MenuFormula,
  MenuFormulaComponent,
  MenuFormulaComponentItem,
} from "@/redux/services/menu/menu_formula.type";
import {
  useDeleteMenuFormulaMutation,
  useDeleteMenuFormulaComponentMutation,
  useDeleteMenuFormulaComponentItemMutation,
  useGetMenuFormulaQuery,
} from "@/redux/services/menu/menu_formula.api";
import ComponentFormModal from "./ComponentFormModal";
import ComponentItemFormModal from "./ComponentItemFormModal";
import ComponentItemOptionModal from "./ComponentItemOptionModal";
import {
  Calendar,
  ChevronDown,
  Clock,
  Leaf,
  Pencil,
  Plus,
  Settings2,
  Trash2,
} from "lucide-react";
import { useAppSelector } from "@/redux/features/hooks";
import { usePermissions } from "@/hooks/usePermissions";
import FormulaComponentItemIngredients from "@/components/inventory/recipes/FormulaComponentItemIngredients";
import toast from "react-hot-toast";

const DAY_LABELS: Record<number, string> = {
  0: "Dim",
  1: "Lun",
  2: "Mar",
  3: "Mer",
  4: "Jeu",
  5: "Ven",
  6: "Sam",
};

const formatTimeDisplay = (time?: string | null): string => {
  if (!time) return "";
  if (time.includes("T")) return time.split("T")[1]?.substring(0, 5) ?? "";
  return time.substring(0, 5);
};

const Section = ({
  title,
  defaultOpen = true,
  children,
  rightSlot,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  rightSlot?: React.ReactNode;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen} asChild>
      <section>
        <div className="flex items-center justify-between border-b border-border">
          <CollapsibleTrigger className="flex-1 flex items-center justify-between py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-card-foreground transition-colors">
            <span>{title}</span>
            <ChevronDown
              className={cn(
                "h-4 w-4 transition-transform",
                open && "rotate-180"
              )}
            />
          </CollapsibleTrigger>
          {rightSlot}
        </div>
        <CollapsibleContent className="pt-4 space-y-3">
          {children}
        </CollapsibleContent>
      </section>
    </Collapsible>
  );
};

const MetaRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="text-card-foreground font-medium">{value}</span>
  </div>
);

interface Props {
  formulaId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (formula: MenuFormula) => void;
}

const FormulaDetailsDrawer: React.FC<Props> = ({
  formulaId,
  isOpen,
  onClose,
  onEdit,
}) => {
  const currency = useRestaurantCurrency();
  const confirm = useConfirm();

  const { data: detail, isFetching, refetch } = useGetMenuFormulaQuery(
    formulaId ?? "",
    { skip: !formulaId }
  );

  const [deleteFormula, { isLoading: isDeleting }] =
    useDeleteMenuFormulaMutation();

  const {
    isOpen: isComponentModalOpen,
    item: selectedComponent,
    openModal: addComponent,
    openEditModal: editComponent,
    closeModal: closeComponentModal,
  } = useModal<MenuFormulaComponent>();

  const [itemModalState, setItemModalState] = useState<{
    isOpen: boolean;
    componentId: string;
    item?: MenuFormulaComponentItem;
  }>({ isOpen: false, componentId: "" });

  const [optionModalItem, setOptionModalItem] =
    useState<MenuFormulaComponentItem | null>(null);

  const handleDelete = async () => {
    if (!detail) return;
    const ok = await confirm({
      title: `Supprimer "${detail.name}" ?`,
      description: "Cette action est irréversible.",
      confirmText: "Supprimer",
      cancelText: "Annuler",
      variant: "destructive",
      icon: <Trash2 className="h-6 w-6" />,
    });
    if (!ok) return;
    try {
      await deleteFormula(detail.id).unwrap();
      toast.success("Formule supprimée");
      onClose();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const sortedComponents = [...(detail?.components ?? [])].sort(
    (a, b) => a.sortOrder - b.sortOrder
  );

  const startTimeDisplay = formatTimeDisplay(detail?.startTime);
  const endTimeDisplay = formatTimeDisplay(detail?.endTime);
  const scheduleText =
    startTimeDisplay && endTimeDisplay
      ? `${startTimeDisplay} - ${endTimeDisplay}`
      : "Toute la journée";

  return (
    <Sheet open={isOpen} onOpenChange={(next) => !next && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col gap-0"
      >
        <header className="flex-shrink-0 px-6 py-4 border-b border-border bg-card">
          <SheetTitle className="text-base">
            {detail ? detail.name : "Détails de la formule"}
          </SheetTitle>
          <SheetDescription hidden>
            Détails et actions pour cette formule.
          </SheetDescription>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-6">
          {isFetching && !detail ? (
            <div className="space-y-3">
              <Skeleton className="h-6 w-2/3" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : detail ? (
            <>
              <Section title="Aperçu">
                {detail.image?.url && (
                  <div className="w-32 h-32 bg-muted/50 rounded-full overflow-hidden border-2 border-border">
                    <img
                      src={getImageUrl(detail.image.url)}
                      alt={detail.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <MetaRow
                    label="Prix"
                    value={
                      <span className="text-primary font-semibold">
                        {formatPrice(detail.price / 100, currency)}
                      </span>
                    }
                  />
                  <MetaRow label="Position" value={detail.sortOrder} />
                </div>
                {detail.description && (
                  <p className="text-sm text-muted-foreground bg-muted/50 rounded-md p-3">
                    {detail.description}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      detail.isActive
                        ? "bg-accent-light text-accent"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {detail.isActive ? "Active" : "Inactive"}
                  </span>
                  {detail.isCurrentlyActive && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-accent-light text-accent">
                      Disponible maintenant
                    </span>
                  )}
                </div>
              </Section>

              <Section title="Disponibilité">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar size={14} />
                  <div className="flex flex-wrap gap-1">
                    {[...(detail.daysOfWeek ?? [])]
                      .sort((a, b) => a - b)
                      .map((day) => (
                        <span
                          key={day}
                          className="px-2 py-0.5 rounded bg-primary/10 text-primary text-xs font-medium"
                        >
                          {DAY_LABELS[day]}
                        </span>
                      ))}
                    {(detail.daysOfWeek?.length ?? 0) === 0 && (
                      <span className="text-xs">Aucun jour défini</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock size={14} />
                  <span>{scheduleText}</span>
                </div>
              </Section>

              <Section
                title={`Composants (${sortedComponents.length})`}
                rightSlot={
                  <ButtonAdd
                    label="Ajouter"
                    type="button"
                    onClick={addComponent}
                  />
                }
              >
                {sortedComponents.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Aucun composant. Ajoutez-en un pour commencer.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {sortedComponents.map((component) => (
                      <ComponentCard
                        key={component.id}
                        component={component}
                        currency={currency}
                        onEdit={() => editComponent(component)}
                        onAddItem={() =>
                          setItemModalState({
                            isOpen: true,
                            componentId: component.id,
                          })
                        }
                        onEditItem={(item) =>
                          setItemModalState({
                            isOpen: true,
                            componentId: component.id,
                            item,
                          })
                        }
                        onManageItemOptions={(item) =>
                          setOptionModalItem(item)
                        }
                        onAfterDelete={refetch}
                      />
                    ))}
                  </div>
                )}
              </Section>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              Impossible de charger les détails.
            </p>
          )}
        </div>

        <footer className="flex-shrink-0 px-6 py-4 border-t border-border bg-card flex justify-end gap-2">
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={!detail || isDeleting}
          >
            <Trash2 className="h-4 w-4" />
            Supprimer
          </Button>
          <Button
            type="button"
            variant="accent"
            onClick={() => detail && onEdit(detail)}
            disabled={!detail}
          >
            <Pencil className="h-4 w-4" />
            Modifier
          </Button>
        </footer>
      </SheetContent>

      {isComponentModalOpen && detail && (
        <ComponentFormModal
          isOpen={isComponentModalOpen}
          onClose={closeComponentModal}
          component={selectedComponent}
          formulaId={detail.id}
          onSuccess={refetch}
        />
      )}

      {itemModalState.isOpen && (
        <ComponentItemFormModal
          isOpen={itemModalState.isOpen}
          onClose={() =>
            setItemModalState({ isOpen: false, componentId: "" })
          }
          item={itemModalState.item}
          componentId={itemModalState.componentId}
          onSuccess={refetch}
        />
      )}

      {optionModalItem && (
        <ComponentItemOptionModal
          isOpen={!!optionModalItem}
          onClose={() => setOptionModalItem(null)}
          componentItem={optionModalItem}
          onSuccess={refetch}
        />
      )}
    </Sheet>
  );
};

// ---------- ComponentCard ----------

interface ComponentCardProps {
  component: MenuFormulaComponent;
  currency: string;
  onEdit: () => void;
  onAddItem: () => void;
  onEditItem: (item: MenuFormulaComponentItem) => void;
  onManageItemOptions: (item: MenuFormulaComponentItem) => void;
  onAfterDelete: () => void;
}

const ComponentCard: React.FC<ComponentCardProps> = ({
  component,
  currency,
  onEdit,
  onAddItem,
  onEditItem,
  onManageItemOptions,
  onAfterDelete,
}) => {
  const [deleteComponent, { isLoading: isDeleting }] =
    useDeleteMenuFormulaComponentMutation();
  const confirm = useConfirm();

  const sortedItems = [...(component.includedItems ?? [])].sort(
    (a, b) => a.sortOrder - b.sortOrder
  );

  const handleDelete = async () => {
    const ok = await confirm({
      title: `Supprimer "${component.name}" ?`,
      description: "Tous les éléments inclus seront retirés.",
      confirmText: "Supprimer",
      cancelText: "Annuler",
      variant: "destructive",
      icon: <Trash2 className="h-6 w-6" />,
    });
    if (!ok) return;
    try {
      await deleteComponent(component.id).unwrap();
      toast.success("Composant supprimé");
      onAfterDelete();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <div className="border border-border rounded-md p-3 space-y-2 bg-muted/30">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm text-card-foreground">
            {component.name}
          </p>
          <div className="mt-1 flex flex-wrap gap-1 text-[11px]">
            <span className="px-1.5 py-0.5 rounded bg-card text-muted-foreground border border-border">
              Qté {component.quantity}
            </span>
            <span className="px-1.5 py-0.5 rounded bg-card text-muted-foreground border border-border">
              Max {component.maxChoiceCount}
            </span>
            <span
              className={`px-1.5 py-0.5 rounded ${
                component.isRequired
                  ? "bg-blue-100 text-blue-800"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {component.isRequired ? "Requis" : "Optionnel"}
            </span>
          </div>
          {component.description && (
            <p className="text-xs text-muted-foreground mt-1">
              {component.description}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            type="button"
            onClick={onEdit}
            className="p-1 rounded hover:bg-card text-muted-foreground hover:text-primary"
            title="Modifier"
          >
            <Pencil size={14} />
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={isDeleting}
            className="p-1 rounded hover:bg-card text-muted-foreground hover:text-danger disabled:opacity-50"
            title="Supprimer"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      <div className="border-t border-border pt-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-muted-foreground">
            Éléments ({sortedItems.length})
          </span>
          <button
            type="button"
            onClick={onAddItem}
            className="flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <Plus size={12} />
            Ajouter
          </button>
        </div>
        {sortedItems.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">
            Aucun élément
          </p>
        ) : (
          <ul className="space-y-1">
            {sortedItems.map((item) => (
              <ComponentItemRow
                key={item.id}
                item={item}
                currency={currency}
                onEdit={() => onEditItem(item)}
                onManageOptions={() => onManageItemOptions(item)}
                onAfterDelete={onAfterDelete}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// ---------- ComponentItemRow ----------

interface ComponentItemRowProps {
  item: MenuFormulaComponentItem;
  currency: string;
  onEdit: () => void;
  onManageOptions: () => void;
  onAfterDelete: () => void;
}

const ComponentItemRow: React.FC<ComponentItemRowProps> = ({
  item,
  currency,
  onEdit,
  onManageOptions,
  onAfterDelete,
}) => {
  const [deleteItem, { isLoading: isDeleting }] =
    useDeleteMenuFormulaComponentItemMutation();
  const confirm = useConfirm();
  const restaurant = useAppSelector((state) => state.user.currentRestaurant);
  const [ingredientsOpen, setIngredientsOpen] = useState(false);
  // Slot recipes are read with inventory.view (SFRESTO-136); editing them needs
  // inventory.manage, which the recipe editor checks itself.
  const { can } = usePermissions();
  const canViewRecipe = can("inventory.view");

  const itemName = item.name ?? item.product?.title ?? "Sans nom";
  const optionCount = item.itemOptions?.length ?? 0;
  const isCustomSlot = !item.product;

  const handleDelete = async () => {
    const ok = await confirm({
      title: `Supprimer "${itemName}" ?`,
      confirmText: "Supprimer",
      cancelText: "Annuler",
      variant: "destructive",
      icon: <Trash2 className="h-6 w-6" />,
    });
    if (!ok) return;
    try {
      await deleteItem(item.id).unwrap();
      toast.success("Élément supprimé");
      onAfterDelete();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <li className="px-2 py-1.5 rounded bg-card">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {(item.image?.url ?? item.product?.image?.url) && (
            <img
              src={getImageUrl(
                item.image?.url ?? item.product?.image?.url
              )}
              alt={itemName}
              className="w-7 h-7 rounded object-cover flex-shrink-0"
            />
          )}
          <span className="text-sm truncate text-card-foreground">
            {itemName}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {item.priceModifier > 0 && (
            <span className="text-xs text-accent font-medium">
              +{displayPrice(item.priceModifier, currency)}
            </span>
          )}
          {isCustomSlot && canViewRecipe && (
            <button
              type="button"
              onClick={() => setIngredientsOpen((o) => !o)}
              className={cn(
                "p-0.5 text-muted-foreground hover:text-primary",
                ingredientsOpen && "text-primary"
              )}
              title="Ingrédients"
            >
              <Leaf size={12} />
            </button>
          )}
          <button
            type="button"
            onClick={onManageOptions}
            className="p-0.5 text-muted-foreground hover:text-primary"
            title="Options"
          >
            <Settings2 size={12} />
          </button>
          <button
            type="button"
            onClick={onEdit}
            className="p-0.5 text-muted-foreground hover:text-primary"
            title="Modifier"
          >
            <Pencil size={12} />
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={isDeleting}
            className="p-0.5 text-muted-foreground hover:text-danger disabled:opacity-50"
            title="Supprimer"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>
      {optionCount > 0 && (
        <div className="flex flex-wrap gap-1 mt-1 ml-9">
          {item.itemOptions!.map((opt) => {
            const title =
              typeof opt.menuItemOption === "object"
                ? opt.menuItemOption?.title
                : undefined;
            if (!title) return null;
            return (
              <span
                key={opt.id}
                className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium"
              >
                {title}
                {opt.isRequired && " *"}
              </span>
            );
          })}
        </div>
      )}
      {isCustomSlot && canViewRecipe && ingredientsOpen && restaurant && (
        <div className="mt-2 ml-9 border-t border-border pt-2">
          <FormulaComponentItemIngredients
            componentItemId={item.id}
            restaurantId={restaurant.id}
          />
        </div>
      )}
    </li>
  );
};

export default FormulaDetailsDrawer;
